import type { SceneBuildManifest } from '../../../types/scene-build-manifest.type';

export type ValidateGlbOutputParams = {
	buffer: ArrayBuffer;
	manifest: SceneBuildManifest | null;
	includeSky: boolean;
	skyExpected: boolean;
};

export type ValidateGlbOutputResult = {
	valid: boolean;
	missing: Array<string>;
};

const parseJsonChunk = (buffer: ArrayBuffer): unknown | null => {
	if (buffer.byteLength < 20) return null;
	const view = new DataView(buffer);
	const jsonChunkLength = view.getUint32(12, true);
	if (jsonChunkLength === 0 || 20 + jsonChunkLength > buffer.byteLength) return null;
	try {
		const jsonBytes = new Uint8Array(buffer, 20, jsonChunkLength);
		return JSON.parse(new TextDecoder().decode(jsonBytes));
	} catch {
		return null;
	}
};

export const validateGlbOutputCfn = (params: ValidateGlbOutputParams): ValidateGlbOutputResult => {
	const { buffer, manifest, includeSky, skyExpected } = params;
	const missing: Array<string> = [];

	if (buffer.byteLength < 12) {
		missing.push('buffer too small to be valid GLB');
		return { valid: false, missing };
	}

	const view = new DataView(buffer);
	const magic = view.getUint32(0, true);
	const version = view.getUint32(4, true);
	const declaredLength = view.getUint32(8, true);

	if (magic !== 0x46546C67) missing.push('invalid GLB magic bytes');
	if (version !== 2) missing.push(`unexpected GLB version ${version}`);
	if (declaredLength !== buffer.byteLength) missing.push(`GLB declared length ${declaredLength} !== actual ${buffer.byteLength}`);

	const gltf = parseJsonChunk(buffer) as any;
	if (!gltf) {
		missing.push('failed to parse GLB JSON chunk');
		return { valid: false, missing };
	}

	if (!Array.isArray(gltf.materials) || gltf.materials.length === 0) {
		missing.push('GLB has no materials');
	}

	if (manifest && manifest.appliances.length > 0) {
		const nodes: Array<{ extras?: { id?: string; type?: string } }> = Array.isArray(gltf.nodes) ? gltf.nodes : [];
		for (const expected of manifest.appliances) {
			const found = nodes.some(
				(n) => n.extras?.type === 'appliance' && n.extras?.id === expected.id
			);
			if (!found) {
				missing.push(`appliance id="${expected.id}" sku="${expected.sku}" not in exported nodes`);
			}
		}
	}

	if (includeSky && skyExpected) {
		const nodes: Array<{ name?: string }> = Array.isArray(gltf.nodes) ? gltf.nodes : [];
		const hasSkyDome = nodes.some((n) => n.name === 'sky-dome');
		if (!hasSkyDome) missing.push('sky-dome node missing from export');
	}

	return { valid: missing.length === 0, missing };
};
